import type { MakeSuggestion } from '#/shared/api/generated/shipment/models'
import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption } from '#/shared/ui/combobox'

import { useAutoSelectExact } from '../model/use-auto-select-exact'
import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useMakeSearch } from '../model/use-make-search'

interface MakeComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onSelect?: (value: string) => void
  label?: string
  placeholder?: string
  error?: boolean
  disabled?: boolean
  className?: string
}

const ALWAYS_PASS_FILTER = () => true

export function MakeCombobox({
  value,
  onValueChange,
  onSelect,
  label,
  placeholder,
  error,
  disabled,
  className,
}: MakeComboboxProps) {
  const freeze = useComboboxFreeze<MakeSuggestion>(value)
  const live = useMakeSearch(value, {
    enabled: freeze.isUserTyped && !freeze.isJustSelected,
  })
  const items = freeze.pickItems(live.items)
  const options: ComboboxOption[] = items.map((it) => ({
    value: it.make,
    label: it.make,
  }))

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    // Re-picking the value that's already committed is a no-op for downstream
    // fields (model/lot/vin stay put).
    const isReselect = option.label === value
    freeze.freeze(option.label, items)
    if (isReselect) return
    onSelect?.(option.value)
  }

  useAutoSelectExact({
    enabled: freeze.isUserTyped && !freeze.isJustSelected,
    value,
    items,
    getLabel: (it) => it.make,
    onMatch: (it) => {
      freeze.freeze(it.make, items)
      onValueChange(it.make)
      onSelect?.(it.make)
    },
  })

  return (
    <Combobox
      label={label}
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      filter={ALWAYS_PASS_FILTER}
      loading={live.isLoading}
      hideEmpty={live.needsMoreInput || !freeze.isUserTyped}
      hideToggle
      error={error}
      disabled={disabled}
      className={className}
      clearable
    />
  )
}
